// The world's meshes in the low-poly look (decision 0029): a chunk's terrain as flat-shaded, vertex-coloured facets,
// and its scatter merged into the same attribute set (position, normal, colour, glow, sway), so a chunk can draw as one
// mesh on the look's one surface material. `sway` is the foliage push's weight (@rpg/look's SWAY_ATTRIBUTE, decision
// 0085): 0 on the terrain and on rigid pieces, rising with height on a piece whose look asks it to sway. The pieces scattered come from small parametric generators (trees, pines,
// bushes, rocks, grass tufts, flowers), built once per variant and placed by copying their vertices: a streaming
// step never builds a piece. three.js is a peer; colours are the game's.
import * as THREE from "three";
import { facet, merge, seeded } from "@rpg/meshkit";
import type { ChunkHeights } from "./world";
import type { ScatterItem } from "./scatter";

/** The generators' kinds. */
export type PieceKind = "tree" | "pine" | "bush" | "rock" | "tuft" | "flower";

/** A piece's look: its generator, its colours (resolved by the game from its palette) and a size factor. */
export interface PieceLook {
  kind: PieceKind;
  colours: readonly THREE.Color[];
  /** Multiplies the piece's base size. Default 1. */
  size?: number;
  /** How much the piece's top sways when pushed, 0 to 1 (a walk-through bush, grass). Default 0: rigid. */
  sway?: number;
}

/** The foliage push's weight attribute (the same name as @rpg/look's SWAY_ATTRIBUTE). */
const SWAY = "sway";

/** The sway weight at height `h` within a piece (0 at its base, 1 at its top): a cantilever's bend under a load at
 *  its tip, h²(3 − h)/2, so the base stays rooted and the top moves most. Times the look's `sway`. */
export function swayWeight(h: number, sway: number): number {
  const t = Math.min(1, Math.max(0, Number.isFinite(h) ? h : 0));
  return Math.min(1, Math.max(0, sway)) * ((t * t * (3 - t)) / 2);
}

const pick = <T>(xs: readonly T[], r: () => number): T => xs[Math.min(xs.length - 1, Math.floor(r() * xs.length))]!;

/** Build one variant of a piece (its base at the origin, about a metre to a few metres tall at size 1). Pure in
 *  (look, seed). */
export function pieceGeometry(look: PieceLook, seed: number): THREE.BufferGeometry {
  const r = seeded(seed);
  const k = look.size ?? 1;
  const c = look.colours;
  const part = (g: THREE.BufferGeometry, colour: THREE.Color, jitter: number, tint = 0.08): THREE.BufferGeometry => facet(g, { colour, jitter, tint, rng: r });
  const parts: THREE.BufferGeometry[] = [];
  switch (look.kind) {
    case "tree": {
      const h = (1.5 + r() * 0.5) * k;
      parts.push(part(new THREE.CylinderGeometry(0.11 * k, 0.2 * k, h, 5).translate(0, h / 2, 0), c[0]!, 0.02));
      const crowns = 2 + Math.floor(r() * 2);
      for (let i = 0; i < crowns; i++) {
        const rad = (0.75 + r() * 0.45) * k * (i ? 0.75 : 1);
        const a = r() * Math.PI * 2;
        const off = i ? 0.55 * k : 0;
        parts.push(part(new THREE.IcosahedronGeometry(rad, 0).scale(1, 0.85, 1).translate(Math.cos(a) * off, h + rad * 0.55 + (i ? 0.25 * k : 0), Math.sin(a) * off), pick(c.slice(1), r), 0.08 * k));
      }
      break;
    }
    case "pine": {
      const h = (0.8 + r() * 0.3) * k;
      parts.push(part(new THREE.CylinderGeometry(0.08 * k, 0.14 * k, h, 5).translate(0, h / 2, 0), c[0]!, 0.01));
      let y = h * 0.8;
      for (const [rad, ht] of [
        [1.1, 1.7],
        [0.82, 1.4],
        [0.52, 1.1],
      ] as const) {
        parts.push(part(new THREE.ConeGeometry(rad * k, ht * k, 7).translate(0, y + (ht * k) / 2, 0), pick(c.slice(1), r), 0.05 * k));
        y += ht * k * 0.55;
      }
      break;
    }
    case "bush": {
      parts.push(part(new THREE.IcosahedronGeometry(0.6 * k, 0).scale(1.1, 0.7, 1).translate(0, 0.3 * k, 0), pick(c, r), 0.07 * k));
      parts.push(part(new THREE.IcosahedronGeometry(0.42 * k, 0).scale(1, 0.75, 1).translate(0.38 * k, 0.24 * k, 0.12 * k), pick(c, r), 0.05 * k));
      break;
    }
    case "rock": {
      parts.push(part(new THREE.DodecahedronGeometry(0.6 * k, 0).scale(1, 0.62, 0.85).translate(0, 0.22 * k, 0), pick(c, r), 0.11 * k, 0.06));
      break;
    }
    case "tuft": {
      for (let i = 0; i < 3; i++) {
        const a = (i / 3) * Math.PI * 2 + r();
        const g = new THREE.ConeGeometry(0.05 * k, (0.35 + r() * 0.2) * k, 3).translate(0, 0.2 * k, 0).rotateZ(Math.cos(a) * 0.35).rotateX(Math.sin(a) * 0.35).translate(Math.cos(a) * 0.07 * k, 0, Math.sin(a) * 0.07 * k);
        parts.push(part(g, pick(c, r), 0, 0.1));
      }
      break;
    }
    case "flower": {
      parts.push(part(new THREE.CylinderGeometry(0.012 * k, 0.018 * k, 0.32 * k, 3).translate(0, 0.16 * k, 0), c[0]!, 0, 0));
      parts.push(part(new THREE.OctahedronGeometry(0.075 * k, 0).translate(0, 0.34 * k, 0), pick(c.slice(1), r), 0.01, 0.05));
      break;
    }
  }
  const g = merge(parts);
  // the sway weight by height above the base (y = 0) up to the top; rigid pieces carry zeros
  const P = g.getAttribute("position");
  const w = new Float32Array(P.count);
  const sway = look.sway ?? 0;
  if (sway > 0) {
    let top = 0;
    for (let i = 0; i < P.count; i++) top = Math.max(top, P.getY(i));
    for (let i = 0; i < P.count; i++) w[i] = top > 0 ? swayWeight(P.getY(i) / top, sway) : 0;
  }
  g.setAttribute(SWAY, new THREE.BufferAttribute(w, 1));
  return g;
}

/** A cache of piece variants: `variants` per look, built once, shared by every chunk. */
export interface PieceCache {
  /** The variant for an item's seed. */
  get(piece: string, seed: number): THREE.BufferGeometry;
  /** Build every variant now (a boot step: no streaming slice ever builds a piece). */
  warm(): void;
  /** Pieces and variants built. */
  report(): { pieces: number; variants: number; vertices: number };
}

/** Variants per piece, from the looks the game passes (piece name → look). Variant geometries are marked
 *  `userData.resident` so `@rpg/stream` never disposes them. */
export function createPieceCache(looks: Readonly<Record<string, PieceLook>>, variants = 4, seed = 1): PieceCache {
  const cache = new Map<string, THREE.BufferGeometry[]>();
  const build = (piece: string): THREE.BufferGeometry[] => {
    let v = cache.get(piece);
    if (v) return v;
    const look = looks[piece];
    if (!look) throw new Error(`world: no look for piece "${piece}"`);
    v = Array.from({ length: variants }, (_, i) => {
      const g = pieceGeometry(look, (seed * 7919 + i * 104729 + piece.length * 31) >>> 0);
      g.userData.resident = true;
      return g;
    });
    cache.set(piece, v);
    return v;
  };
  return {
    get: (piece, s) => {
      const v = build(piece);
      return v[s % v.length]!;
    },
    warm: () => void Object.keys(looks).forEach(build),
    report: () => {
      let n = 0;
      let verts = 0;
      for (const v of cache.values()) for (const g of v) {
        n++;
        verts += g.getAttribute("position").count;
      }
      return { pieces: cache.size, variants: n, vertices: verts };
    },
  };
}

/** A chunk's terrain as facets: two triangles per heightfield cell in world space, each coloured by `colourAt(x, z)`
 *  at its centroid (so a path's edge follows its triangles, not whole cells), times a small seeded tint. */
export function terrainGeometry(h: ChunkHeights, colourAt: (x: number, z: number) => THREE.Color, o: { tint?: number; seed?: number } = {}): THREE.BufferGeometry {
  const n = h.cols;
  const cell = h.size / n;
  const x0 = h.cx - h.size / 2;
  const z0 = h.cz - h.size / 2;
  const tris = n * n * 2;
  const pos = new Float32Array(tris * 9);
  const col = new Float32Array(tris * 9);
  const r = seeded(o.seed ?? 1);
  const tint = o.tint ?? 0.06;
  const H = (ix: number, iz: number): number => h.heights[iz * (n + 1) + ix]!;
  let p = 0;
  const vert = (ix: number, iz: number): void => {
    pos[p++] = x0 + ix * cell;
    pos[p++] = H(ix, iz);
    pos[p++] = z0 + iz * cell;
  };
  let c = 0;
  for (let iz = 0; iz < n; iz++)
    for (let ix = 0; ix < n; ix++) {
      // the same diagonal as the heightfield collider (and the nav geometry): (ix, iz) (ix, iz+1) (ix+1, iz) faces up
      vert(ix, iz);
      vert(ix, iz + 1);
      vert(ix + 1, iz);
      vert(ix + 1, iz);
      vert(ix, iz + 1);
      vert(ix + 1, iz + 1);
      for (let t = 0; t < 2; t++) {
        // centroids: (ix, iz) (ix, iz+1) (ix+1, iz), then (ix+1, iz) (ix, iz+1) (ix+1, iz+1)
        const base = colourAt(x0 + (ix + (t ? 2 / 3 : 1 / 3)) * cell, z0 + (iz + (t ? 2 / 3 : 1 / 3)) * cell);
        const k = 1 + (r() * 2 - 1) * tint;
        for (let v = 0; v < 3; v++) {
          col[c++] = base.r * k;
          col[c++] = base.g * k;
          col[c++] = base.b * k;
        }
      }
    }
  const g = new THREE.BufferGeometry();
  g.setAttribute("position", new THREE.BufferAttribute(pos, 3));
  g.setAttribute("color", new THREE.BufferAttribute(col, 3));
  g.computeVertexNormals();
  g.setAttribute("glow", new THREE.BufferAttribute(new Float32Array(tris * 3), 1));
  g.setAttribute(SWAY, new THREE.BufferAttribute(new Float32Array(tris * 3), 1));
  return g;
}

/** Scatter items placed and merged into one geometry (position, normal, colour, glow, sway): each item's variant scaled,
 *  turned by its yaw and moved to its place. Copies vertices; builds nothing. */
export function placeItems(items: readonly ScatterItem[], geometryOf: (it: ScatterItem) => THREE.BufferGeometry): THREE.BufferGeometry {
  const geos = items.map(geometryOf);
  const total = geos.reduce((a, g) => a + g.getAttribute("position").count, 0);
  const pos = new Float32Array(total * 3);
  const nor = new Float32Array(total * 3);
  const col = new Float32Array(total * 3);
  const glow = new Float32Array(total);
  const sway = new Float32Array(total);
  let o = 0;
  items.forEach((it, i) => {
    const g = geos[i]!;
    const P = g.getAttribute("position");
    const N = g.getAttribute("normal");
    const C = g.getAttribute("color");
    const G = g.getAttribute("glow");
    const S = g.getAttribute(SWAY);
    const cs = Math.cos(it.yaw);
    const sn = Math.sin(it.yaw);
    for (let v = 0; v < P.count; v++, o++) {
      const x = P.getX(v) * it.scale;
      const y = P.getY(v) * it.scale;
      const z = P.getZ(v) * it.scale;
      pos[o * 3] = it.x + x * cs + z * sn;
      pos[o * 3 + 1] = it.y + y;
      pos[o * 3 + 2] = it.z - x * sn + z * cs;
      const nx = N.getX(v);
      const nz = N.getZ(v);
      nor[o * 3] = nx * cs + nz * sn;
      nor[o * 3 + 1] = N.getY(v);
      nor[o * 3 + 2] = -nx * sn + nz * cs;
      col[o * 3] = C.getX(v);
      col[o * 3 + 1] = C.getY(v);
      col[o * 3 + 2] = C.getZ(v);
      glow[o] = G ? G.getX(v) : 0;
      sway[o] = S ? S.getX(v) : 0;
    }
  });
  const out = new THREE.BufferGeometry();
  out.setAttribute("position", new THREE.BufferAttribute(pos, 3));
  out.setAttribute("normal", new THREE.BufferAttribute(nor, 3));
  out.setAttribute("color", new THREE.BufferAttribute(col, 3));
  out.setAttribute("glow", new THREE.BufferAttribute(glow, 1));
  out.setAttribute(SWAY, new THREE.BufferAttribute(sway, 1));
  return out;
}
